import type React from "react";
import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface AcceptResultDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  isLoading?: boolean;
  initialReason?: string | null;
}

export const AcceptResultDialog: React.FC<AcceptResultDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  initialReason,
}) => {
  const [reason, setReason] = useState(initialReason ?? "");

  useEffect(() => {
    if (isOpen) {
      setReason(initialReason ?? "");
    } else {
      setReason("");
    }
  }, [isOpen, initialReason]);

  const trimmed = reason.trim();
  const isSubmitDisabled = isLoading;

  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    onSubmit(trimmed);
  };

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          onClose();
        }
      }}
    >
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" aria-hidden="true" />
            Accept Result
          </AlertDialogTitle>
        </AlertDialogHeader>
        <div className="space-y-2">
          <label htmlFor="accept-result-reason" className="text-sm font-medium">
            Reason (optional)
          </label>
          <Textarea
            id="accept-result-reason"
            placeholder="Optionally provide a short reason for accepting this result"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isLoading}
            className="min-h-[100px]"
            autoFocus
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitDisabled}>
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
